import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Mail,
  Sparkles,
  Send,
  Star,
  MessageSquare,
  AtSign,
  Youtube,
  Music2,
  Instagram,
  Facebook,
  Inbox as InboxIcon,
  Circle,
  Globe,
  Lock
} from 'lucide-react';

const platformOptions = [
  { value: 'all', label: 'All Platforms', icon: Globe },
  { value: 'youtube', label: 'YouTube', icon: Youtube },
  { value: 'tiktok', label: 'TikTok', icon: Music2 },
  { value: 'instagram', label: 'Instagram', icon: Instagram },
  { value: 'facebook', label: 'Facebook', icon: Facebook }
];

const inboxFilters = [
  { value: 'all', label: 'All', icon: InboxIcon },
  { value: 'dms', label: 'DMs', icon: Send },
  { value: 'comments', label: 'Comments', icon: MessageSquare },
  { value: 'mentions', label: 'Mentions', icon: AtSign },
  { value: 'priority', label: 'Priority', icon: Star }
];

const previewRows = [
  {
    id: 'a1',
    platform: { icon: Instagram, color: 'pink', label: 'Instagram' },
    user: 'audience.member',
    preview: 'Loved this clip — when is part 2 dropping?',
    type: { label: 'Comment', icon: MessageSquare, color: 'cyan' },
    status: { label: 'Needs Reply', color: 'amber' },
    timestamp: '12m'
  },
  {
    id: 'a2',
    platform: { icon: Music2, color: 'purple', label: 'TikTok' },
    user: 'fan_username',
    preview: 'Tagged you in a viral remix — going off right now',
    type: { label: 'Mention', icon: AtSign, color: 'violet' },
    status: { label: 'Trending', color: 'orange' },
    timestamp: '1h'
  },
  {
    id: 'a3',
    platform: { icon: Facebook, color: 'blue', label: 'Facebook' },
    user: 'top.supporter',
    preview: 'Hey — would love to support your next live, what do you need?',
    type: { label: 'DM', icon: Send, color: 'green' },
    status: { label: 'Priority', color: 'red' },
    timestamp: '3h'
  },
  {
    id: 'a4',
    platform: { icon: Youtube, color: 'red', label: 'YouTube' },
    user: 'subscriber.handle',
    preview: 'The OBS settings tutorial saved my livestream — thank you 🙌',
    type: { label: 'Comment', icon: MessageSquare, color: 'cyan' },
    status: { label: 'Unread', color: 'blue' },
    timestamp: '5h'
  },
  {
    id: 'a5',
    platform: { icon: Instagram, color: 'pink', label: 'Instagram' },
    user: 'creator.fanclub',
    preview: 'Fan account just hit 10k — all of us love your content',
    type: { label: 'Mention', icon: AtSign, color: 'violet' },
    status: { label: 'Trending', color: 'orange' },
    timestamp: '8h'
  },
  {
    id: 'a6',
    platform: { icon: Music2, color: 'purple', label: 'TikTok' },
    user: 'longtime.viewer',
    preview: 'New DM — interested in collab + sponsorship details',
    type: { label: 'DM', icon: Send, color: 'green' },
    status: { label: 'Priority', color: 'red' },
    timestamp: '1d'
  }
];

const InboxRow = ({ row, onSelect }) => {
  const PIcon = row.platform.icon;
  const TIcon = row.type.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(row)}
      className="w-full flex items-center gap-3 px-4 py-2 border-b border-white/[0.04] last:border-0 text-left transition-colors hover:bg-white/[0.025] focus:bg-white/[0.04] focus:outline-none"
      data-testid={`audience-inbox-row-${row.id}`}
    >
      <Circle className="h-1.5 w-1.5 fill-green-400 text-green-400 shrink-0" />

      <div className={`w-7 h-7 rounded-lg bg-${row.platform.color}-500/10 border border-white/5 flex items-center justify-center shrink-0`}>
        <PIcon className={`h-3.5 w-3.5 text-${row.platform.color}-400`} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold text-white truncate">@{row.user}</p>
          <div className={`hidden md:flex items-center gap-1 px-1.5 py-0.5 rounded bg-${row.type.color}-500/10 shrink-0`}>
            <TIcon className={`h-2.5 w-2.5 text-${row.type.color}-400`} />
            <span className={`text-[9px] uppercase tracking-wide text-${row.type.color}-300`}>{row.type.label}</span>
          </div>
        </div>
        <p className="text-xs text-[#8A93A6] truncate leading-snug mt-0.5">{row.preview}</p>
      </div>

      <Badge variant="outline" className={`hidden md:inline-flex text-[10px] border-${row.status.color}-500/30 text-${row.status.color}-300 uppercase tracking-wide shrink-0`}>
        {row.status.label}
      </Badge>

      <span className="text-[10px] text-[#555E6E] tabular-nums shrink-0 w-6 text-right">{row.timestamp}</span>
    </button>
  );
};

export default function ConnectAudienceInbox() {
  const [platform, setPlatform] = useState('all');
  const [inboxFilter, setInboxFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  const activeFilterLabel = inboxFilters.find((f) => f.value === inboxFilter)?.label || 'All';
  const activePlatform = platformOptions.find((p) => p.value === platform);
  const ActivePlatformIcon = activePlatform?.icon || Globe;

  return (
    <div className="container mx-auto p-6 max-w-7xl" data-testid="connect-audience-inbox-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <Mail className="h-6 w-6 text-green-400" />
          <h1 className="text-3xl font-bold text-white">Audience Inbox</h1>
          <Badge variant="outline" className="text-[10px] border-green-500/30 text-green-300 uppercase tracking-wider font-semibold">
            External
          </Badge>
        </div>
        <p className="text-[#8A93A6] max-w-2xl">
          Audience conversations, comments, DMs, mentions, and engagement across connected platforms.
        </p>
      </div>

      {/* Info Card */}
      <div
        className="mb-5 rounded-xl border border-white/5 bg-gradient-to-br from-green-500/[0.05] via-transparent to-cyan-500/[0.04] px-5 py-3.5"
        data-testid="audience-inbox-info-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-green-500/[0.08] border border-green-500/15 flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4 text-green-300" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Unified Audience Relationship Inbox</p>
            <p className="text-xs text-[#8A93A6] leading-relaxed max-w-3xl">
              Audience Inbox centralizes messages, comments, mentions, and engagement signals from connected platforms into one relationship layer.
            </p>
          </div>
        </div>
      </div>

      {/* Unified Inbox */}
      <Card className="glass border-white/5 overflow-hidden" data-testid="audience-inbox-list">
        {/* Sticky toolbar */}
        <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-b border-white/5 bg-[#0F1117]/95 backdrop-blur supports-[backdrop-filter]:bg-[#0F1117]/80">
          <div className="flex items-center gap-2">
            <InboxIcon className="h-4 w-4 text-green-400" />
            <span className="text-sm font-semibold text-white">Inbox</span>
            <span className="text-xs text-[#8A93A6]">· {activePlatform?.label}</span>
          </div>

          <div className="flex flex-wrap items-center gap-1" data-testid="audience-inbox-filters">
            {inboxFilters.map((f) => {
              const FIcon = f.icon;
              return (
                <button
                  key={f.value}
                  onClick={() => setInboxFilter(f.value)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    inboxFilter === f.value
                      ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                      : 'text-[#8A93A6] hover:text-white border border-transparent'
                  }`}
                  data-testid={`audience-inbox-filter-${f.value}`}
                >
                  <FIcon className="h-3.5 w-3.5" />
                  {f.label}
                </button>
              );
            })}

            {/* Vertical divider */}
            <div className="w-px h-5 bg-white/10 mx-1.5" />

            {/* Platform filter (in toolbar) */}
            <div data-testid="audience-inbox-platform-dropdown">
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger
                  className="h-7 w-[160px] bg-white/[0.03] border border-white/10 text-white text-xs hover:bg-white/[0.06] focus:ring-0 focus:ring-offset-0"
                  data-testid="audience-inbox-platform-trigger"
                >
                  <div className="flex items-center gap-1.5">
                    <ActivePlatformIcon className="h-3.5 w-3.5 text-green-400" />
                    <SelectValue placeholder="All Platforms" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {platformOptions.map((p) => {
                    const POIcon = p.icon;
                    return (
                      <SelectItem
                        key={p.value}
                        value={p.value}
                        data-testid={`audience-inbox-platform-option-${p.value}`}
                      >
                        <div className="flex items-center gap-2">
                          <POIcon className="h-3.5 w-3.5" />
                          {p.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Dense rows */}
        <div data-testid="audience-inbox-rows">
          {previewRows.map((row) => (
            <InboxRow key={row.id} row={row} onSelect={setSelected} />
          ))}
        </div>

        {/* Compact footer */}
        <div className="border-t border-white/5 px-5 py-4 bg-white/[0.015] flex items-center gap-3">
          <Lock className="h-3.5 w-3.5 text-[#8A93A6] shrink-0" />
          <p className="text-xs text-[#8A93A6]">
            <span className="text-[#C7CED9] font-medium">Read-only preview.</span>{' '}
            Once you connect platforms, DMs, comments, mentions, and priority engagement signals from connected platforms will flow into this unified relationship inbox{inboxFilter !== 'all' ? ` (currently filtered by ${activeFilterLabel})` : ''}.
          </p>
        </div>
      </Card>

      <div className="mt-8 text-center">
        <p className="text-xs text-[#555E6E]">
          Strong creator relationships build strong audiences.
        </p>
      </div>

      {/* Read-only Detail Drawer */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md bg-[#0B0D12] border-l border-white/5 text-white" data-testid="audience-inbox-detail-drawer">
          {selected && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded bg-${selected.platform.color}-500/10`}>
                    <selected.platform.icon className={`h-3 w-3 text-${selected.platform.color}-400`} />
                    <span className={`text-[10px] uppercase tracking-wide text-${selected.platform.color}-300`}>{selected.platform.label}</span>
                  </div>
                  <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded bg-${selected.type.color}-500/10`}>
                    <selected.type.icon className={`h-3 w-3 text-${selected.type.color}-400`} />
                    <span className={`text-[10px] uppercase tracking-wide text-${selected.type.color}-300`}>{selected.type.label}</span>
                  </div>
                  <Badge variant="outline" className={`text-[10px] border-${selected.status.color}-500/30 text-${selected.status.color}-300 uppercase tracking-wide`}>
                    {selected.status.label}
                  </Badge>
                </div>
                <SheetTitle className="text-white">@{selected.user}</SheetTitle>
                <SheetDescription className="text-[#8A93A6]">Received {selected.timestamp} ago · Read-only preview</SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-4">
                <div className="rounded-lg border border-white/5 bg-white/[0.02] p-4">
                  <p className="text-sm text-[#C7CED9] leading-relaxed">"{selected.preview}"</p>
                </div>
                <p className="text-xs text-[#555E6E] italic">
                  Full conversation thread, replies, reactions, and one-click responses will render here once platforms are connected.
                </p>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
